import MapLogger from '../utils/MapLogger';
import MapErrorHandler from '../utils/MapErrorHandler';

/**
 * Map Layer Manager
 * Handles adding, removing, and updating different map layers
 */
class MapLayerManager {
  /**
   * Add floor layers for residential projects
   * @param {Object} mapRef - Map reference
   * @param {Object} floorData - Floor data
   * @param {number} floorId - Floor ID
   * @param {Object} options - Additional options
   */
  static async addFloorLayers(mapRef, floorData, floorId, options = {}) {
    try {
      MapLogger.logOperationStart('add-floor-layers', { floorId, options });
      
      if (!mapRef.current || !mapRef.current.isStyleLoaded()) {
        throw new Error('Map is not ready');
      }

      // Add data source
      await this.addFloorDataSource(mapRef, floorData, floorId);

      // Add fill layer
      await this.addFloorFillLayer(mapRef, floorId, options);

      // Add outline layer
      await this.addFloorOutlineLayer(mapRef, floorId, options);

      // Add labels layer
      await this.addFloorLabelsLayer(mapRef, floorId, options);

      // Add prices layer (if enabled)
      if (options.showPrices) {
        await this.addFloorPricesLayer(mapRef, floorId, options);
      }

      MapLogger.logOperationEnd('add-floor-layers', true, { floorId });

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'floor-layers', { floorId, options });
      MapLogger.logOperationEnd('add-floor-layers', false, { error: error.message });
    }
  }

  /**
   * Add parcel layers for regular projects
   * @param {Object} mapRef - Map reference
   * @param {Object|string} parcelData - Parcel data or direct URL
   * @param {Object} options - Additional options
   */
  static async addParcelLayers(mapRef, parcelData, options = {}) {
    try {
      MapLogger.logOperationStart('add-parcel-layers', { options });
      
      if (!mapRef.current || !mapRef.current.isStyleLoaded()) {
        throw new Error('Map is not ready');
      }

      // Add data source
      await this.addParcelDataSource(mapRef, parcelData);

      // Add fill layer
      await this.addParcelFillLayer(mapRef, options);

      // Add outline layer
      await this.addParcelOutlineLayer(mapRef, options);

      // Add labels layer
      await this.addParcelLabelsLayer(mapRef, options);

      // Add prices layer (if enabled)
      if (options.showPrices) {
        await this.addParcelPricesLayer(mapRef, options);
      }

      MapLogger.logOperationEnd('add-parcel-layers', true, { options });

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'parcel-layers', { options });
      MapLogger.logOperationEnd('add-parcel-layers', false, { error: error.message });
    }
  }

  /**
   * Add floor data source
   * @param {Object} mapRef - Map reference
   * @param {Object} floorData - Floor data
   * @param {number} floorId - Floor ID
   */
  static async addFloorDataSource(mapRef, floorData, floorId) {
    try {
      // Remove existing source if it exists
      if (mapRef.current.getSource('parcels')) {
        mapRef.current.removeSource('parcels');
      }

      // Add new source
      mapRef.current.addSource('parcels', {
        type: 'geojson',
        data: floorData
      });

      MapLogger.logLayerOperation('floor-data-source', 'Added successfully', { floorId });

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'floor-data-source', { floorId });
    }
  }

  /**
   * Add parcel data source
   * @param {Object} mapRef - Map reference
   * @param {Object|string} parcelData - Parcel data or direct URL
   */
  static async addParcelDataSource(mapRef, parcelData) {
    try {
      // Remove existing source if it exists
      if (mapRef.current.getSource('parcels')) {
        mapRef.current.removeSource('parcels');
      }

      // Add new source
      if (typeof parcelData === 'string') {
        // Direct URL
        mapRef.current.addSource('parcels', {
          type: 'geojson',
          data: parcelData
        });
      } else {
        // Direct data
        mapRef.current.addSource('parcels', {
          type: 'geojson',
          data: parcelData
        });
      }

      MapLogger.logLayerOperation('parcel-data-source', 'Added successfully');

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'parcel-data-source');
    }
  }

  /**
   * Add floor fill layer
   * @param {Object} mapRef - Map reference
   * @param {number} floorId - Floor ID
   * @param {Object} options - Additional options
   */
  static async addFloorFillLayer(mapRef, floorId, options = {}) {
    try {
      const layerId = 'parcels-fill';
      
      // Remove existing layer if it exists
      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.removeLayer(layerId);
      }

      // Add new layer
      mapRef.current.addLayer({
        id: layerId,
        type: 'fill',
        source: 'parcels',
        filter: ['==', 'floor_id', floorId],
        paint: {
          'fill-color': this.getStatusColorExpression(),
          'fill-opacity': options.fillOpacity || 0.7
        }
      });

      MapLogger.logLayerOperation('floor-fill-layer', 'Added successfully', { floorId });

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'floor-fill-layer', { floorId });
    }
  }

  /**
   * Add parcel fill layer
   * @param {Object} mapRef - Map reference
   * @param {Object} options - Additional options
   */
  static async addParcelFillLayer(mapRef, options = {}) {
    try {
      const layerId = 'parcels-fill';
      
      // Remove existing layer if it exists
      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.removeLayer(layerId);
      }

      // Add new layer
      mapRef.current.addLayer({
        id: layerId,
        type: 'fill',
        source: 'parcels',
        paint: {
          'fill-color': this.getStatusColorExpression(),
          'fill-opacity': options.fillOpacity || 0.7
        }
      });

      MapLogger.logLayerOperation('parcel-fill-layer', 'Added successfully');

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'parcel-fill-layer');
    }
  }

  /**
   * Add floor outline layer
   * @param {Object} mapRef - Map reference
   * @param {number} floorId - Floor ID
   * @param {Object} options - Additional options
   */
  static async addFloorOutlineLayer(mapRef, floorId, options = {}) {
    try {
      const layerId = 'parcels-outline';
      
      // Remove existing layer if it exists
      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.removeLayer(layerId);
      }

      // Add new layer
      mapRef.current.addLayer({
        id: layerId,
        type: 'line',
        source: 'parcels',
        filter: ['==', 'floor_id', floorId],
        paint: {
          'line-color': options.outlineColor || '#ffffff',
          'line-width': options.outlineWidth || 1
        }
      });

      MapLogger.logLayerOperation('floor-outline-layer', 'Added successfully', { floorId });

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'floor-outline-layer', { floorId });
    }
  }

  /**
   * Add parcel outline layer
   * @param {Object} mapRef - Map reference
   * @param {Object} options - Additional options
   */
  static async addParcelOutlineLayer(mapRef, options = {}) {
    try {
      const layerId = 'parcels-outline';
      
      // Remove existing layer if it exists
      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.removeLayer(layerId);
      }

      // Add new layer
      mapRef.current.addLayer({
        id: layerId,
        type: 'line',
        source: 'parcels',
        paint: {
          'line-color': options.outlineColor || '#ffffff',
          'line-width': options.outlineWidth || 1
        }
      });

      MapLogger.logLayerOperation('parcel-outline-layer', 'Added successfully');

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'parcel-outline-layer');
    }
  }

  /**
   * Add floor labels layer
   * @param {Object} mapRef - Map reference
   * @param {number} floorId - Floor ID
   * @param {Object} options - Additional options
   */
  static async addFloorLabelsLayer(mapRef, floorId, options = {}) {
    try {
      const layerId = 'parcels-labels';
      
      // Remove existing layer if it exists
      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.removeLayer(layerId);
      }

      // Add new layer
      mapRef.current.addLayer({
        id: layerId,
        type: 'symbol',
        source: 'parcels',
        filter: ['==', 'floor_id', floorId],
        layout: {
          'text-field': ['get', 'unit_number'],
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-size': options.textSize || 12,
          'text-allow-overlap': true,
          'text-ignore-placement': false
        },
        paint: {
          'text-color': options.textColor || '#ffffff',
          'text-halo-color': options.textHaloColor || '#000000',
          'text-halo-width': options.textHaloWidth || 1
        }
      });

      MapLogger.logLayerOperation('floor-labels-layer', 'Added successfully', { floorId });

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'floor-labels-layer', { floorId });
    }
  }

  /**
   * Add parcel labels layer
   * @param {Object} mapRef - Map reference
   * @param {Object} options - Additional options
   */
  static async addParcelLabelsLayer(mapRef, options = {}) {
    try {
      const layerId = 'parcels-labels';
      
      // Remove existing layer if it exists
      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.removeLayer(layerId);
      }

      // Add new layer
      mapRef.current.addLayer({
        id: layerId,
        type: 'symbol',
        source: 'parcels',
        layout: {
          'text-field': ['get', 'parcel_number'],
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-size': options.textSize || 12,
          'text-allow-overlap': true,
          'text-ignore-placement': false
        },
        paint: {
          'text-color': options.textColor || '#ffffff',
          'text-halo-color': options.textHaloColor || '#000000',
          'text-halo-width': options.textHaloWidth || 1
        }
      });

      MapLogger.logLayerOperation('parcel-labels-layer', 'Added successfully');

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'parcel-labels-layer');
    }
  }

  /**
   * Add floor prices layer
   * @param {Object} mapRef - Map reference
   * @param {number} floorId - Floor ID
   * @param {Object} options - Additional options
   */
  static async addFloorPricesLayer(mapRef, floorId, options = {}) {
    try {
      const layerId = 'parcels-prices';
      
      // Remove existing layer if it exists
      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.removeLayer(layerId);
      }

      // Add new layer
      mapRef.current.addLayer({
        id: layerId,
        type: 'symbol',
        source: 'parcels',
        filter: ['==', 'floor_id', floorId],
        layout: {
          'text-field': ['concat', '$', ['get', 'price_with_tax']],
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-size': options.priceTextSize || 10,
          'text-allow-overlap': true,
          'text-ignore-placement': false
        },
        paint: {
          'text-color': options.priceTextColor || '#ffffff',
          'text-halo-color': options.priceTextHaloColor || '#000000',
          'text-halo-width': options.priceTextHaloWidth || 1
        }
      });

      MapLogger.logLayerOperation('floor-prices-layer', 'Added successfully', { floorId });

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'floor-prices-layer', { floorId });
    }
  }

  /**
   * Add parcel prices layer
   * @param {Object} mapRef - Map reference
   * @param {Object} options - Additional options
   */
  static async addParcelPricesLayer(mapRef, options = {}) {
    try {
      const layerId = 'parcels-prices';
      
      // Remove existing layer if it exists
      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.removeLayer(layerId);
      }

      // Add new layer
      mapRef.current.addLayer({
        id: layerId,
        type: 'symbol',
        source: 'parcels',
        layout: {
          'text-field': ['concat', '$', ['get', 'price_with_tax']],
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-size': options.priceTextSize || 10,
          'text-allow-overlap': true,
          'text-ignore-placement': false
        },
        paint: {
          'text-color': options.priceTextColor || '#ffffff',
          'text-halo-color': options.priceTextHaloColor || '#000000',
          'text-halo-width': options.priceTextHaloWidth || 1
        }
      });

      MapLogger.logLayerOperation('parcel-prices-layer', 'Added successfully');

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'parcel-prices-layer');
    }
  }

  /**
   * Remove all layers
   * @param {Object} mapRef - Map reference
   * @param {Array} layerIds - Layer IDs to remove
   */
  static async removeLayers(mapRef, layerIds = []) {
    try {
      const defaultLayers = [
        'parcels-fill',
        'parcels-outline',
        'parcels-labels',
        'parcels-prices',
        'floor-basemap-layer'
      ];

      const layersToRemove = layerIds.length > 0 ? layerIds : defaultLayers;

      layersToRemove.forEach(layerId => {
        if (mapRef.current.getLayer(layerId)) {
          mapRef.current.removeLayer(layerId);
        }
      });

      MapLogger.logLayerOperation('remove-layers', 'Removed successfully', { layerIds: layersToRemove });

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'remove-layers', { layerIds });
    }
  }

  /**
   * Remove all sources
   * @param {Object} mapRef - Map reference
   * @param {Array} sourceIds - Source IDs to remove
   */
  static async removeSources(mapRef, sourceIds = []) {
    try {
      const defaultSources = ['parcels', 'floor-basemap'];

      const sourcesToRemove = sourceIds.length > 0 ? sourceIds : defaultSources;
      const map = mapRef.current;

      sourcesToRemove.forEach(sourceId => {
        if (!map.getSource(sourceId)) return;

        const style = map.getStyle();
        const dependentLayers = (style && style.layers ? style.layers : [])
          .filter(layer => layer.source === sourceId)
          .map(layer => layer.id);

        dependentLayers.forEach(layerId => {
          if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
          }
        });

        map.removeSource(sourceId);
      });

      MapLogger.logLayerOperation('remove-sources', 'Removed successfully', { sourceIds: sourcesToRemove });

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'remove-sources', { sourceIds });
    }
  }

  /**
   * Get status color expression
   * @returns {Array} - Mapbox color expression
   */
  static getStatusColorExpression() {
    return [
      'case',
      ['==', ['get', 'sales_status'], 'Available'], '#4CAF50',
      ['==', ['get', 'sales_status'], 'Reserved'], '#FF9800',
      ['==', ['get', 'sales_status'], 'Sold'], '#F44336',
      ['==', ['get', 'sales_status'], 'Not Available'], '#9E9E9E',
      '#E0E0E0'
    ];
  }

  /**
   * Update layer properties
   * @param {Object} mapRef - Map reference
   * @param {string} layerId - Layer ID
   * @param {Object} properties - New properties
   */
  static async updateLayerProperties(mapRef, layerId, properties) {
    try {
      if (!mapRef.current.getLayer(layerId)) {
        throw new Error(`Layer ${layerId} not found`);
      }

      Object.keys(properties).forEach(key => {
        mapRef.current.setPaintProperty(layerId, key, properties[key]);
      });

      MapLogger.logLayerOperation('update-layer-properties', 'Updated successfully', { layerId, properties });

    } catch (error) {
      MapErrorHandler.handleLayerError(error, 'update-layer-properties', { layerId, properties });
    }
  }

  /**
   * Check if layer exists
   * @param {Object} mapRef - Map reference
   * @param {string} layerId - Layer ID
   * @returns {boolean} - Layer existence
   */
  static layerExists(mapRef, layerId) {
    try {
      return mapRef.current && mapRef.current.getLayer(layerId) !== undefined;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if source exists
   * @param {Object} mapRef - Map reference
   * @param {string} sourceId - Source ID
   * @returns {boolean} - Source existence
   */
  static sourceExists(mapRef, sourceId) {
    try {
      return mapRef.current && mapRef.current.getSource(sourceId) !== undefined;
    } catch (error) {
      return false;
    }
  }
}

export default MapLayerManager;
